import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  fetchSummary,
  fetchModules,
  fetchRecords,
} from "@/lib/api";
import { compactNumber, formatNumber } from "@/lib/format";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { ArrowUpRight, MapPinned, BadgePercent, ShoppingBag, Users, ClipboardList, LayoutDashboard, Sparkles } from "lucide-react";

const ICONS = {
  wilayah: MapPinned,
  discount: BadgePercent,
  penjualan: ShoppingBag,
  pelanggan: Users,
  program: ClipboardList,
};

const CHART_COLORS = ["#0047FF", "#F53B16", "#F59E0B", "#10B981", "#F97316", "#0F172A"];

const MONTH_ORDER = ["JAN", "FEB", "MAR", "MARET", "APR", "APRIL", "MEI", "JUN", "JUNI", "JUL", "JULI", "AGST", "AUG", "SEP", "SEPT", "OKT", "OCT", "NOV", "DES", "DEC"];

function monthKey(v) {
  if (!v) return "ZZZ";
  const s = String(v).toUpperCase().slice(0, 3);
  const idx = MONTH_ORDER.findIndex((m) => m.startsWith(s));
  return idx >= 0 ? String(idx).padStart(2, "0") + "_" + v : "99_" + v;
}

export default function Overview() {
  const { data: summary = [] } = useQuery({ queryKey: ["summary"], queryFn: fetchSummary });
  const { data: modules = [] } = useQuery({ queryKey: ["modules"], queryFn: fetchModules });
  const { data: penjualan = [] } = useQuery({
    queryKey: ["records", "penjualan"],
    queryFn: () => fetchRecords("penjualan"),
  });
  const { data: pelanggan = [] } = useQuery({
    queryKey: ["records", "pelanggan"],
    queryFn: () => fetchRecords("pelanggan"),
  });
  const { data: discount = [] } = useQuery({
    queryKey: ["records", "discount"],
    queryFn: () => fetchRecords("discount"),
  });

  // Compute KPIs
  const totalPenjualan = penjualan.reduce(
    (a, r) => a + (Number(r.data?.net_penjualan) || 0),
    0
  );
  const totalQty = penjualan.reduce((a, r) => a + (Number(r.data?.qty) || 0), 0);
  const totalPelBaru = pelanggan.reduce(
    (a, r) => a + (Number(r.data?.pelanggan_baru) || 0),
    0
  );
  const totalDiskon = discount.reduce(
    (a, r) => a + (Number(r.data?.total_transaksi) || 0),
    0
  );

  // Sales trend per month (aggregated across cabang)
  const monthAgg = {};
  penjualan.forEach((r) => {
    const b = r.data?.bulan || "?";
    monthAgg[b] = (monthAgg[b] || 0) + (Number(r.data?.net_penjualan) || 0);
  });
  const trendData = Object.keys(monthAgg)
    .map((k) => ({ bulan: k, penjualan: monthAgg[k], _sort: monthKey(k) }))
    .sort((a, b) => a._sort.localeCompare(b._sort));

  // Sales by cabang
  const cabAgg = {};
  penjualan.forEach((r) => {
    const c = r.data?.cabang || "?";
    cabAgg[c] = (cabAgg[c] || 0) + (Number(r.data?.net_penjualan) || 0);
  });
  const cabData = Object.keys(cabAgg)
    .map((k) => ({ cabang: k, nilai: cabAgg[k] }))
    .sort((a, b) => b.nilai - a.nilai);

  // Discount by kategori (pie)
  const katAgg = {};
  discount.forEach((r) => {
    const k = r.data?.kategori_disc || "?";
    katAgg[k] = (katAgg[k] || 0) + (Number(r.data?.total_transaksi) || 0);
  });
  const discPie = Object.keys(katAgg).map((k) => ({ name: k, value: katAgg[k] }));

  return (
    <div className="p-8 lg:p-10 max-w-[1600px]">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="mono text-[11px] uppercase tracking-widest text-slate-400 mb-2">
            Dashboard · Marketing MKI
          </div>
          <h1
            data-testid="overview-title"
            className="font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900"
          >
            Ikhtisar Kinerja
          </h1>
          <p className="text-slate-500 mt-2 max-w-xl">
            Ringkasan seluruh modul data pemasaran, penjualan, dan operasional.
            Kelola, edit, unggah, dan ekspor per modul.
          </p>
        </div>
        <div className="hidden md:block text-right">
          <div className="mono text-[11px] uppercase tracking-widest text-slate-400">
            Periode Fokus
          </div>
          <div className="font-display font-black text-2xl">MEI 2026</div>
          <button
            data-testid="overview-ai-btn"
            onClick={() => window.__openAi && window.__openAi("overview")}
            className="btn-sharp mt-2 inline-flex items-center gap-1.5 bg-slate-900 text-white text-xs font-semibold px-3 py-2 hover:bg-[#0047FF] transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Minta Analisis AI
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard
          label="Total Net Penjualan"
          value={"Rp " + compactNumber(totalPenjualan)}
          delta={`${penjualan.length} baris data`}
          testId="kpi-penjualan"
          accent="#0047FF"
        />
        <KpiCard
          label="Total Qty Terjual"
          value={formatNumber(totalQty)}
          delta="Unit produk"
          testId="kpi-qty"
          accent="#F53B16"
        />
        <KpiCard
          label="Pelanggan Baru"
          value={formatNumber(totalPelBaru)}
          delta={`${pelanggan.length} rekap cabang-bulan`}
          testId="kpi-pelanggan"
          accent="#10B981"
        />
        <KpiCard
          label="Nilai Diskon"
          value={"Rp " + compactNumber(totalDiskon)}
          delta={`${discount.length} kategori`}
          testId="kpi-diskon"
          accent="#F59E0B"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div
          data-testid="chart-trend"
          className="lg:col-span-2 bg-white border border-slate-200 p-6"
        >
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="mono text-[11px] uppercase tracking-widest text-slate-400">
                Time series
              </div>
              <h2 className="font-display font-black text-lg tracking-tight">
                Tren Net Penjualan per Bulan
              </h2>
            </div>
          </div>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={trendData} margin={{ left: 6, right: 12 }}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="bulan" stroke="#64748b" tick={{ fontSize: 12 }} />
                <YAxis
                  stroke="#64748b"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(v) => compactNumber(v)}
                />
                <Tooltip
                  formatter={(v) => "Rp " + Number(v).toLocaleString("id-ID")}
                  contentStyle={{ background: "#0f172a", border: 0, color: "#fff", borderRadius: 2 }}
                  labelStyle={{ color: "#fff" }}
                />
                <Line
                  type="monotone"
                  dataKey="penjualan"
                  stroke="#0047FF"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: "#0047FF" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div
          data-testid="chart-cabang"
          className="bg-white border border-slate-200 p-6"
        >
          <div className="mono text-[11px] uppercase tracking-widest text-slate-400">
            Ranking
          </div>
          <h2 className="font-display font-black text-lg tracking-tight mb-4">
            Penjualan per Cabang
          </h2>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={cabData} layout="vertical" margin={{ left: 8 }}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tickFormatter={(v) => compactNumber(v)} stroke="#64748b" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="cabang" stroke="#64748b" tick={{ fontSize: 11 }} width={50} />
                <Tooltip
                  formatter={(v) => "Rp " + Number(v).toLocaleString("id-ID")}
                  contentStyle={{ background: "#0f172a", border: 0, color: "#fff", borderRadius: 2 }}
                />
                <Bar dataKey="nilai" fill="#0047FF" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Second row: pie + modules grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div
          data-testid="chart-diskon"
          className="bg-white border border-slate-200 p-6"
        >
          <div className="mono text-[11px] uppercase tracking-widest text-slate-400">
            Distribusi
          </div>
          <h2 className="font-display font-black text-lg tracking-tight mb-4">
            Diskon per Kategori
          </h2>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={discPie}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={2}
                >
                  {discPie.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => "Rp " + Number(v).toLocaleString("id-ID")}
                  contentStyle={{ background: "#0f172a", border: 0, color: "#fff", borderRadius: 2 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 p-6">
          <div className="mono text-[11px] uppercase tracking-widest text-slate-400">
            Modul Data
          </div>
          <h2 className="font-display font-black text-lg tracking-tight mb-4">
            Akses Cepat
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {modules.map((m) => {
              const Icon = ICONS[m.id] || LayoutDashboard;
              const s = summary.find((x) => x.id === m.id);
              return (
                <Link
                  key={m.id}
                  to={`/modul/${m.id}`}
                  data-testid={`quick-${m.id}`}
                  className="group border border-slate-200 p-4 hover:border-[#0047FF] hover:shadow-sm transition-all flex items-start gap-3"
                >
                  <div className="w-10 h-10 bg-slate-100 group-hover:bg-[#0047FF]/10 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-slate-600 group-hover:text-[#0047FF]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-sm truncate">{m.name}</div>
                      <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-[#0047FF] shrink-0" />
                    </div>
                    <div className="text-xs text-slate-500 mt-1 line-clamp-1">
                      {m.description}
                    </div>
                    <div className="mono text-[11px] text-slate-400 mt-2 uppercase tracking-wider">
                      {s?.count ?? 0} baris
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, delta, testId, accent }) {
  return (
    <div
      data-testid={testId}
      className="bg-white border border-slate-200 p-5 relative overflow-hidden"
    >
      <div
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{ background: accent }}
      />
      <div className="mono text-[11px] uppercase tracking-widest text-slate-400 mb-2">
        {label}
      </div>
      <div className="font-display font-black text-3xl tracking-tight tabular">
        {value}
      </div>
      <div className="text-xs text-slate-500 mt-2">{delta}</div>
    </div>
  );
}
